import { saveAs } from 'file-saver';
import millify from 'millify';
import { Theme } from 'ui';
import { captureException } from '@shared/libs/sentry';
import { FiltersForm } from '@shared/types/forms';
import { getNewDate, toISOString } from '@utils/dates';
import {
  extractFourHourBlockDateRange,
  extractFourHourBlockTimeRange,
  formatSpikeDateRange,
} from '@utils/datesToSpike';
import { numberToPercentage, removeDecimals } from '@utils/numbers';
import { isEmptyString } from '@utils/strings';
import {
  Case,
  GetCaseAggregateResultsResponse,
} from 'services/ClaimscoreApiService/Cases/types';
import { DashboardFilters } from 'services/ClaimscoreApiService/shared/types';

export const getSpikeColorByIndex = (theme: Theme) => [
  theme.palette.orange[600],
  theme.palette.violet[600],
  theme.palette.red[600],
];

const EndOfDayTime = { hour: 23, minute: 59, second: 59, millisecond: 0 };

export const mapDatesToTimezoneDates = (startDate = '', endDate = '') => {
  if (isEmptyString(startDate) || isEmptyString(endDate))
    return { startDateUTC: '', endDateUTC: '' };

  const startDateUTC = toISOString(new Date(startDate));
  const endDateUTC = getNewDate(endDate);
  endDateUTC.setUTCHours(
    EndOfDayTime.hour,
    EndOfDayTime.minute,
    EndOfDayTime.second,
    EndOfDayTime.millisecond,
  );
  return { startDateUTC, endDateUTC: toISOString(endDateUTC) };
};

export const mapFormToPayload = (
  filters: FiltersForm,
  selectedCase: Case,
): DashboardFilters => {
  const {
    deductionCodes,
    determinations,
    payoutRange,
    scoreRange,
    unitRange,
    startDate,
    endDate,
    claimTypes,
    useRealTimeClaims,
    files,
    claimscoreResult,
    submissionType,
    accessType,
    documentationProvided,
    entityType,
  } = filters;

  const hasDetermination = determinations && determinations.length > 0;
  const hasUnitRange = unitRange && unitRange.length > 0;
  const hasPayoutRange = payoutRange && payoutRange.length > 0;
  const hasScoreRange = scoreRange && scoreRange.length > 0;
  const hasClaimTypes = claimTypes && claimTypes.length > 0;
  const hasDeductionCode = deductionCodes && deductionCodes.length > 0;
  const excludeRealTime = useRealTimeClaims === false;
  const hasFiles = files && files.length > 0;
  const hasClaimscoreResult = claimscoreResult && claimscoreResult.length > 0;
  const hasSubmissionType = submissionType && submissionType.length > 0;
  const hasAccessType = accessType && accessType.length > 0;
  const hasDocumentationProvided = documentationProvided !== undefined;
  const hasEntityType = entityType && entityType.length > 0;
  const { startDateUTC, endDateUTC } = mapDatesToTimezoneDates(
    startDate,
    endDate,
  );

  const getInitialDate = (date: string) =>
    toISOString(selectedCase ? new Date(date) : new Date());

  return {
    dateRange: {
      startDate:
        startDateUTC || getInitialDate(selectedCase?.claimSubmissionStartDate),
      endDate:
        endDateUTC || getInitialDate(selectedCase?.claimSubmissionEndDate),
    },
    determinations: hasDetermination ? determinations : undefined,
    unitRange: hasUnitRange
      ? {
          min: Number(unitRange?.[0]),
          max: Number(unitRange?.[1]),
        }
      : undefined,
    payoutRange: hasPayoutRange
      ? {
          min: Number(payoutRange?.[0] || 0) * 100,
          max: Number(payoutRange?.[1] || 0) * 100,
        }
      : undefined,
    scoreRange: hasScoreRange
      ? {
          min: Number(scoreRange?.[0]),
          max: Number(scoreRange?.[1]),
        }
      : undefined,
    deductionCodes: hasDeductionCode ? deductionCodes : undefined,
    claimTypes: hasClaimTypes ? claimTypes : undefined,
    useRealTimeClaims: !excludeRealTime,
    fileIDs: hasFiles ? files : undefined,
    claimscoreResult: hasClaimscoreResult ? claimscoreResult : undefined,
    submissionType: hasSubmissionType ? submissionType : undefined,
    accessType: hasAccessType ? accessType : undefined,
    documentationProvided: hasDocumentationProvided
      ? documentationProvided
      : undefined,
    entityType: hasEntityType ? entityType : undefined,
  };
};

interface Spike {
  spikeID: string;
  title: string;
  totalClaims: number;
  vsAverage: number;
  borderColor: string;
  claimData: { key: string; value: string; color?: string }[];
  blocks: { title: string; info: { value: string; link: string }[] }[];
  date?: string;
  subdateRange?: string;
  timeInterval?: string;
}

export const keysToParse = [
  'unitRange',
  'payoutRange',
  'scoreRange',
  'claimTypes',
  'deductionCodes',
  'determinations',
  'files',
  'claimscoreResult',
  'accessType',
];

export const decodeSearchParams = (searchParams: URLSearchParams) =>
  [...searchParams.entries()].reduce((acc, [key, val]) => {
    if (val === 'true' || val === 'false') {
      return {
        ...acc,
        [key]: val === 'true',
      };
    }
    if (!keysToParse.includes(key)) {
      return {
        ...acc,
        [key]: val,
      };
    }
    try {
      return {
        ...acc,
        [key]: val.includes(',') ? val.split(',') : [val],
      };
    } catch (error) {
      captureException(error);
      return {
        ...acc,
        [key]: undefined,
      };
    }
  }, {});

export const mapAggregateToSpikes = (
  dataToSpike: GetCaseAggregateResultsResponse,
  theme: Theme,
  isWordingActive?: boolean,
): Spike[] => {
  const { buckets, timeInterval } = dataToSpike;
  const spikeBuckets = buckets.filter((bucket) => bucket.isSpike);
  const totalBuckets = buckets.filter(
    (bucket) =>
      bucket.deductionCodesCount && bucket.deductionCodesCount.length > 0,
  );

  const averageClaims =
    buckets.reduce(
      (acc, curr) =>
        acc + curr.totalApproved + curr.totalPending + curr.totalRejected,
      0,
    ) / (totalBuckets.length || 1);

  const spikeColors = getSpikeColorByIndex(theme);
  return spikeBuckets.map((bucket, index) => {
    const {
      totalApproved,
      totalPending,
      totalRejected,
      sourceCount,
      deductionCodesCount,
      totalClaims,
    } = bucket;
    const approvedPercentage = numberToPercentage(totalApproved, totalClaims);
    const pendingPercentage = numberToPercentage(totalPending, totalClaims);
    const rejectedPercentage = numberToPercentage(totalRejected, totalClaims);

    const claimData = isWordingActive
      ? [
          {
            key: 'Score \u2267700',
            value: approvedPercentage === 0 ? 'N/A' : `${approvedPercentage}%`,
            color: theme.palette.primary.main,
          },
          {
            key: 'Score <700',
            value: rejectedPercentage === 0 ? 'N/A' : `${rejectedPercentage}%`,
            color: theme.palette.error.main,
          },
          {
            key: 'Not Analyzed',
            value: pendingPercentage === 0 ? 'N/A' : `${pendingPercentage}%`,
            color: theme.palette.text.secondary,
          },
        ]
      : [
          {
            key: 'Approved',
            value: approvedPercentage === 0 ? 'N/A' : `${approvedPercentage}%`,
          },
          {
            key: 'Pending',
            value: pendingPercentage === 0 ? 'N/A' : `${pendingPercentage}%`,
          },
          {
            key: 'Rejected',
            value: rejectedPercentage === 0 ? 'N/A' : `${rejectedPercentage}%`,
          },
        ];

    const deductionBlockTitle = isWordingActive
      ? 'Top Deduction Codes'
      : 'Top Rejected';

    const isFourHourBlock = timeInterval === 'fourHourBlock';
    const dateRange = isFourHourBlock
      ? extractFourHourBlockDateRange(bucket.startDate)
      : formatSpikeDateRange(bucket.startDate, bucket.endDate, timeInterval);

    return {
      title: `Spike ${index + 1}`,
      spikeID: bucket.bucketID,
      totalClaims,
      vsAverage: removeDecimals((totalClaims / averageClaims) * 100),
      borderColor: spikeColors[index],
      claimData,
      ...(isWordingActive && {
        date: dateRange,
        ...(isFourHourBlock && {
          subdateRange: extractFourHourBlockTimeRange(bucket.startDate),
        }),
      }),
      blocks: [
        {
          title: 'Top Sources',
          info: (
            sourceCount?.sort((a, b) => b.count - a.count).slice(0, 3) || []
          ).map(({ source, count }) => ({
            link: source,
            value: millify(count),
          })),
        },
        {
          title: deductionBlockTitle,
          info: (
            deductionCodesCount
              ?.sort((a, b) => b.count - a.count)
              .slice(0, 3) || []
          ).map(({ deductionCode, count }) => ({
            link: deductionCode,
            value: millify(count),
          })),
        },
      ],
    };
  });
};

const fileTypeMap = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  zip: 'application/zip',
};

export const generateReportCaseFile = ({
  caseName,
  todayDate,
  file,
  fileType,
}: {
  caseName: string;
  todayDate: string;
  file: string;
  fileType: 'pdf' | 'csv' | 'zip';
}) => {
  const blob = new Blob([file], {
    type: fileTypeMap[fileType],
  });
  saveAs(blob, `ClaimScore Report - ${caseName} ${todayDate}.${fileType}`);
};

export const getInitialDate = (date: string, option: string) => date || option;
